import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, CircleCheck, GraduationCap, Play, Plus, Rocket, ShieldAlert, Trash2,
} from 'lucide-react';
import { get, post, del, ApiError } from '@/lib/api';
import { runBulkPreflightWithRecovery, type StoreLike } from '@/lib/preflight-recovery';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { Thumbnail } from '@/components/domain/Thumbnail';
import { PreflightCellRow } from '@/components/domain/PreflightCellRow';
import {
  gcsToPath, fontNames,
  type TeamStoreDetail, type TeamStoreLogo, type FontsPayload,
} from '@/components/domain/StoreEditorDrawer';
import { BOTH_GENDER_NOTE, STORE_GENDERS, mapGenderToken, splitSportAndGender } from '@/lib/store-gender';
import { sortCreateStoreTemplates } from '@/lib/create-store-templates';
import { resolveCreateStoreArtwork, type ArtworkMode } from '@/lib/create-store-artwork';
import { lineupCoverage, sortArtworkTemplates, sportToArtworkTemplateId } from '@/lib/store-template-kinds';
import { productLineupHref, templateEditorHref } from '@/lib/template-editor-query';
import { colorSeparationError, snapToPalette, normalizeHex } from '@/lib/color-separation';
import { teamIdentityOf, planColourPrefill, prefillMascot, colourPrefillNote } from '@/lib/team-identity-prefill';
import { PERSONALIZATION_MODE_OPTIONS, createStorePersonalization, type PersonalizationMode } from '@/lib/personalization-mode';

// Native 6-step Create Team Store wizard — full replacement for the
// admin-dashboard wizard (no click-away). Steps 1–2 collect school/template/
// colors locally; entering step 3 writes the draft teamStores doc (status
// pending_review, TS-number assigned) and steps 3–6 operate on the live doc,
// so the flow can be resumed any time via /create-store?store={id}.

type School = {
  placeId?: string;
  id?: string;
  ghostfit_id?: string;
  school_name?: string;
  name?: string;
  city?: string;
  state?: string;
  mascot?: string;
  primary_color?: string;
  primary_color_name?: string;
  secondary_color?: string;
  secondary_color_name?: string;
  tertiary_color?: string;
  tertiary_color_name?: string;
};

type ScoutSport = { sport_id: string; display_name: string; gender?: string };
type SportEntry = string | { id?: string; sport_id?: string; display_name?: string; name?: string; sport?: string; gender?: string };
type SportsPayload = SportEntry[] | { sports?: SportEntry[] | Record<string, unknown> };

// scout returns sports as a map keyed by sport_id ({ football_boys: {...} });
// older shapes as an array. Keep the sport_id — it is the key v4 builds the
// store URL from. Display names are for the rep only.
function normalizeSports(data?: SportsPayload): ScoutSport[] {
  const raw = Array.isArray(data) ? data : data?.sports;
  const out: ScoutSport[] = [];
  const push = (id: unknown, v: unknown) => {
    const sportId = typeof id === 'string' ? id : '';
    if (!sportId) return;
    const o = (v && typeof v === 'object' ? v : {}) as { display_name?: string; name?: string; gender?: string };
    out.push({ sport_id: sportId, display_name: o.display_name || o.name || sportId, gender: o.gender });
  };
  if (Array.isArray(raw)) {
    for (const s of raw) {
      if (typeof s === 'string') push(s, {});
      else if (s && typeof s === 'object') push((s as { sport_id?: string; id?: string }).sport_id || (s as { id?: string }).id, s);
    }
  } else if (raw && typeof raw === 'object') {
    for (const [key, v] of Object.entries(raw)) push((v as { sport_id?: string })?.sport_id || key, v);
  }
  return out.sort((a, b) => a.display_name.localeCompare(b.display_name));
}

// Scout keys a coed program plain ("football") and genders the rest
// ("football_boys"), so boys and girls share one display name — tag them or
// the rep cannot tell the two rows apart.
function sportOptionLabel(s: ScoutSport): string {
  const g = mapGenderToken(s.gender) || splitSportAndGender(s.sport_id).gender;
  return g === 'boys' || g === 'girls' ? `${s.display_name} (${g})` : s.display_name;
}

type StoreTemplate = {
  id: string;
  name?: string;
  description?: string;
  kind?: string;
  sport?: string;
  styleCount?: number;
  styles?: { styleNumber?: string }[];
  brands?: string[];
  brandTag?: string;
  active?: boolean;
};

type PaletteColor = { id?: string; name?: string; hex?: string; color?: string };

const STEPS = ['School & Sport', 'Template & Colors', 'Logos', 'Text', 'Preflight', 'Launch'] as const;

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'store';
}

function errMsg(e: unknown, fallback: string): string {
  const body = (e as ApiError).body as { message?: string; error?: string } | null;
  return body?.message || body?.error || fallback;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------

export default function CreateStore() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const resumeId = params.get('store');

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  // Step 1 state
  const [schoolQuery, setSchoolQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [school, setSchool] = useState<School | null>(null);
  const [sport, setSport] = useState('');
  // The Scout sport_id behind `sport`. '' = "Other sport…" free text, which
  // leaves the server to derive a sport_id from sportType + gender.
  const [scoutSportId, setScoutSportId] = useState('');
  const [fundraiserName, setFundraiserName] = useState('');
  const [startDate, setStartDate] = useState(isoDate(new Date()));
  const [adminEmail, setAdminEmail] = useState('');
  const [salesRepEmail, setSalesRepEmail] = useState('');
  const [salesRepName, setSalesRepName] = useState('');
  const [gender, setGender] = useState('');

  // Step 2 state
  const [templateId, setTemplateId] = useState('general');
  const [artworkMode, setArtworkMode] = useState<ArtworkMode>('auto');
  // Per-store name-and-number opt-in. Writes `personalization.hanger` on the
  // teamStores doc, which is what createproducts-v4 reads to offer paid
  // (Personalized) sizes. Default ON (Grant 2026-09-25) — untick to opt out.
  const [personalizationHanger, setPersonalizationHanger] = useState(true);
  // The four-way select replaces the checkbox once the controls switch is on
  // (GET /api/personalization/mode-controls). It always builds the ladder, so an
  // Off store can be turned on later without a rebuild.
  const [personalizationMode, setPersonalizationMode] = useState<PersonalizationMode>('both');
  const { data: modeControls } = useSWR<{ enabled: boolean }>('/api/personalization/mode-controls', get);
  const modeControlsOn = modeControls?.enabled === true;
  const [pickedArtworkId, setPickedArtworkId] = useState('');
  // School colours are free hex (pickers, 2026-09-09); palette families are derived
  // for garment colour-match by snapToPalette, never chosen directly.
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [tertiary, setTertiary] = useState('');
  // What the pre-fill last wrote into each colour (lib/team-identity-prefill.ts), so a sport
  // change can tell an untouched pre-fill from a colour the operator typed.
  const prefilledColours = useRef({ primary: '', secondary: '', tertiary: '' });

  // Created store
  const [storeId, setStoreId] = useState<string | null>(resumeId);
  const storeKey = storeId ? `/api/active-stores/${storeId}` : null;
  const { data: store, mutate: refreshStore } = useSWR<TeamStoreDetail>(storeKey, get);

  // Resume: jump straight into the live-doc phase.
  useEffect(() => {
    if (resumeId && step === 0) setStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(schoolQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [schoolQuery]);

  const { data: schoolResults, isLoading: searching } = useSWR<{ schools: School[] }>(
    debouncedQuery.length >= 2 && !school ? `/api/scout/schools?search=${encodeURIComponent(debouncedQuery)}&limit=12` : null,
    get
  );

  const schoolId = school?.ghostfit_id || school?.placeId || school?.id;
  const { data: sportsData } = useSWR<SportsPayload>(
    schoolId ? `/api/scout/schools/${encodeURIComponent(String(schoolId))}/sports` : null,
    get,
    { shouldRetryOnError: false }
  );
  const sports = useMemo(() => normalizeSports(sportsData), [sportsData]);
  // The picked sport's Scout team identity (spec 2026-10-22 §5): the colours and mascot a live
  // store confirmed for this team. It wins over the school record below.
  const teamIdentity = useMemo(() => teamIdentityOf(sportsData, scoutSportId), [sportsData, scoutSportId]);

  const { data: templatesData } = useSWR<{ templates: StoreTemplate[] }>(step === 1 ? '/api/store-templates' : null, get);
  const createTemplates = useMemo(
    () => sortCreateStoreTemplates(templatesData?.templates || []),
    [templatesData],
  );
  const artworkTemplates = useMemo(
    () => sortArtworkTemplates(templatesData?.templates || []),
    [templatesData],
  );
  const artworkChoice = useMemo(
    () => resolveCreateStoreArtwork({ mode: artworkMode, sport, pickedArtworkId }),
    [artworkMode, sport, pickedArtworkId],
  );
  const derivedArtworkId = sportToArtworkTemplateId(sport);
  const productTpl = createTemplates.find((t) => t.id === templateId);
  const resolvedArtworkId = artworkChoice.artworkTemplateId
    || (artworkMode === 'auto' ? derivedArtworkId : '')
    || '';
  const artworkTpl = artworkTemplates.find((t) => t.id === resolvedArtworkId);
  const coverage = lineupCoverage(productTpl?.styles || [], artworkTpl?.styles || []);
  const { data: paletteData } = useSWR<{ colors?: PaletteColor[] } | PaletteColor[]>(step === 1 ? '/api/school-colors' : null, get);
  const palette = useMemo(() => {
    const arr = Array.isArray(paletteData) ? paletteData : paletteData?.colors;
    return (arr || []).map((c) => ({ id: c.id, name: c.name, hex: c.hex || c.color }));
  }, [paletteData]);

  // Pre-fill: the picked sport's team identity first, then the school record's stored hexes —
  // scout colours may not sit on a palette entry, and that is fine now: the exact hex drives the
  // artwork. A colour the operator changed is never overwritten.
  useEffect(() => {
    if (!school) return;
    const { next, filled } = planColourPrefill({
      current: { primary, secondary, tertiary }, filled: prefilledColours.current, team: teamIdentity, school,
    });
    prefilledColours.current = filled;
    if (next.primary !== primary) setPrimary(next.primary);
    if (next.secondary !== secondary) setSecondary(next.secondary);
    if (next.tertiary !== tertiary) setTertiary(next.tertiary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school, teamIdentity]);

  const colorError = useMemo(() => colorSeparationError({ primary, secondary, tertiary }), [primary, secondary, tertiary]);
  // The note under "School colors": the team's live store only while a field still holds its value.
  const colourNote = colourPrefillNote({ current: { primary, secondary, tertiary }, team: teamIdentity, school });
  const snapped = useMemo(() => snapToPalette([primary, secondary, tertiary], palette), [primary, secondary, tertiary, palette]);

  function pickSchool(s: School) {
    setSchool(s);
    // The sport list is this school's — a sport_id (and any sport/gender it
    // implied) picked for the previous school addresses no program on the
    // new one.
    setScoutSportId('');
    setSport('');
    setGender('');
    setSchoolQuery(s.school_name || s.name || '');
    if (!fundraiserName) setFundraiserName(`${s.school_name || s.name || ''}${sport ? ` ${sport}` : ''} Team Store`.trim());
  }

  const splitSport = splitSportAndGender(sport);
  const resolvedGender = gender || splitSport.gender;
  // A store with no Scout school or no sport is refused by the server (the
  // ghostfit_id and sport_id are what the v4 store URL is built from), so stop
  // the wizard here rather than at the create call.
  const step1Ok = fundraiserName.trim().length > 0 && !!startDate && !!resolvedGender
    && !!school?.ghostfit_id && (!!scoutSportId || splitSport.sport.length > 0);
  const step2Ok = !!(templateId || 'general') && !colorError;

  async function createDraft() {
    setBusy('create');
    try {
      const name = school?.school_name || school?.name || fundraiserName;
      const id = `ts-${slug(name)}-${slug(sport || 'team')}-${Date.now().toString(36)}`;
      const r = await post<{ id: string; storeNumberDisplay: string }>('/api/team-stores', {
        id,
        fundraiserName: fundraiserName.trim(),
        schoolName: school?.school_name || school?.name || undefined,
        ghostfit_id: school?.ghostfit_id || undefined,
        sportType: splitSport.sport || undefined,
        sport_id: scoutSportId || undefined,
        gender: resolvedGender || undefined,
        startDate,
        // Team stores never carry an end date (Grant 2026-09-16): they stay
        // open and are paid out each quarter. The server forces null as well.
        endDate: null,
        commissionRate: 0.10,
        salesRepCommissionRate: 0.07,
        productTemplateId: templateId || 'general',
        useDesignTemplates: artworkChoice.useDesignTemplates,
        personalization: createStorePersonalization({ controlsOn: modeControlsOn, hanger: personalizationHanger, mode: personalizationMode }),
        ...(artworkChoice.artworkTemplateId ? { artworkTemplateId: artworkChoice.artworkTemplateId } : {}),
        mascot: prefillMascot(teamIdentity, school),
        adminEmail: adminEmail.trim() || undefined,
        salesRepEmail: salesRepEmail.trim() || undefined,
        salesRepName: salesRepName.trim() || undefined,
        primaryColor: primary,
        primaryColorId: snapped[0]?.id,
        primaryColorName: snapped[0]?.name,
        secondaryColor: secondary,
        secondaryColorId: snapped[1]?.id,
        secondaryColorName: snapped[1]?.name,
        tertiaryColor: tertiary,
        tertiaryColorId: snapped[2]?.id,
        tertiaryColorName: snapped[2]?.name,
      });
      setStoreId(r.id);
      setParams({ store: r.id }, { replace: true });
      toast.success(`Draft created — ${r.storeNumberDisplay}`);
      setStep(2);
    } catch (e) {
      toast.error(errMsg(e, 'Could not create the store'));
    } finally {
      setBusy(null);
    }
  }

  async function approve() {
    if (!store) return;
    setBusy('approve');
    try {
      const r = await post<{ productsCreated?: number; collectionUrl?: string | null }>(
        `/api/team-stores/${store.id}/approve`, {}
      );
      toast.success(`Store approved — ${r.productsCreated ?? 0} products being created`);
      navigate('/active-stores');
    } catch (e) {
      toast.error(errMsg(e, 'Approve failed'));
    } finally {
      setBusy(null);
    }
  }

  async function overridePreflight() {
    if (!store) return;
    const reason = prompt('Override reason (recorded on the store):');
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('A reason is required to override preflight');
      return;
    }
    setBusy('override');
    try {
      await post(`/api/team-stores/${store.id}/override-preflight`, { reason: reason.trim() });
      toast.success('Override applied — store is approvable');
      await refreshStore();
    } catch (e) {
      toast.error(errMsg(e, 'Override failed'));
    } finally {
      setBusy(null);
    }
  }

  // ---- preflight rollup for steps 5/6 ----
  const logos = store?.logos ?? [];
  const cellStats = useMemo(() => {
    let total = 0, pass = 0;
    for (const l of logos) {
      for (const e of Object.values(l.preflight ?? {})) {
        total++;
        if (e.status === 'pass') pass++;
      }
    }
    return { total, pass, allPass: total > 0 && pass === total };
  }, [logos]);

  const liveStep = step >= 2; // operating on the created doc

  return (
    <div className="mx-auto max-w-[860px] px-7 py-6">
      <PageHeader
        title="Create Team Store"
        subtitle={store ? `${store.storeNumberDisplay || store.id} · ${store.fundraiserName || ''}` : 'School lookup → colors → logos → preflight → launch, all in the console'}
      />

      {/* Stepper */}
      <ol className="mb-6 flex items-center gap-1">
        {STEPS.map((label, i) => {
          const done = i < step;
          const active = i === step;
          const clickable = done || (liveStep && i >= 2);
          return (
            <li key={label} className="flex flex-1 items-center gap-1 last:flex-none">
              <button
                disabled={!clickable}
                onClick={() => clickable && setStep(i)}
                className={`flex items-center gap-1.5 rounded-full py-1 pl-1.5 pr-3 text-[11.5px] font-medium transition-colors ${active ? 'bg-primary text-white shadow-glow' : done ? 'bg-primary/10 text-primary' : 'bg-black/[0.04] text-subtle'} ${clickable ? '' : 'cursor-default'}`}
              >
                <span className={`flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-semibold ${active ? 'bg-white/25' : done ? 'bg-primary text-white' : 'bg-black/[0.07]'}`}>
                  {done ? <Check className="h-3 w-3" aria-hidden /> : i + 1}
                </span>
                <span className="hidden md:block">{label}</span>
              </button>
              {i < STEPS.length - 1 && <span className="h-px flex-1 bg-black/[0.07]" />}
            </li>
          );
        })}
      </ol>

      {/* ---- Step 1: School & Sport ---- */}
      {step === 0 && (
        <div className="space-y-5 rounded-[14px] border border-black/[0.05] bg-surface p-6 shadow-card">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">School</label>
            <div className="relative">
              <Input
                value={schoolQuery}
                onChange={(e) => { setSchoolQuery(e.target.value); setSchool(null); setScoutSportId(''); setSport(''); setGender(''); }}
                placeholder="Search schools by name, city…"
                className="h-9 pl-8 text-[13px]"
              />
              <GraduationCap className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden />
            </div>
            {!school && debouncedQuery.length >= 2 && (
              <div className="mt-1.5 max-h-[240px] overflow-y-auto rounded-[10px] border border-black/[0.06] bg-surface shadow-raised">
                {searching ? (
                  <div className="space-y-1.5 p-2.5"><Skeleton className="h-5 w-full" /><Skeleton className="h-5 w-2/3" /></div>
                ) : (schoolResults?.schools || []).length === 0 ? (
                  <p className="p-3 text-[11.5px] text-muted">No schools found. Every store needs a Scout school — add the school in Scout first, then search again.</p>
                ) : (
                  (schoolResults?.schools || []).map((s) => (
                    <button
                      key={String(s.placeId || s.ghostfit_id || s.id)}
                      onClick={() => pickSchool(s)}
                      className="flex w-full items-center gap-2 border-b border-divider px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-bg/70"
                    >
                      <span className="flex-1 truncate text-[12.5px]">{s.school_name || s.name}</span>
                      <span className="text-[11px] text-subtle">{[s.city, s.state].filter(Boolean).join(', ')}</span>
                      {s.primary_color && <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: s.primary_color }} />}
                    </button>
                  ))
                )}
              </div>
            )}
            {school && (
              <div className="mt-2 flex items-center gap-2 rounded-[10px] bg-primary/[0.05] px-3 py-2">
                <Check className="h-4 w-4 text-primary" aria-hidden />
                <span className="flex-1 truncate text-[12.5px] font-medium">{school.school_name || school.name}</span>
                <span className="text-[11px] text-muted">{[school.city, school.state].filter(Boolean).join(', ')}</span>
                <button className="text-[11px] text-primary underline-offset-2 hover:underline" onClick={() => { setSchool(null); setSchoolQuery(''); setScoutSportId(''); setSport(''); setGender(''); }}>change</button>
              </div>
            )}
            {school && !school.ghostfit_id && (
              <p className="mt-1.5 text-[11px] text-danger">This school has no Scout id yet. Add or fix it in Scout, then pick it again.</p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-sport" className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Sport</label>
              <select
                id="create-store-sport"
                className="h-8 w-full rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px] disabled:opacity-50"
                value={scoutSportId}
                disabled={!school}
                onChange={(e) => {
                  const next = e.target.value;
                  setScoutSportId(next);
                  const chosen = sports.find((s) => s.sport_id === next);
                  if (!chosen) return;
                  // The sport_id carries the gender ("football_boys"); sportType
                  // must stay the bare sport, so split it the way the server does.
                  const split = splitSportAndGender(chosen.sport_id);
                  setSport(split.sport);
                  if (split.gender) setGender(split.gender);
                  else { const g = mapGenderToken(chosen.gender); if (g) setGender(g); }
                  if (school) setFundraiserName(`${school.school_name || school.name} ${split.sport} Team Store`);
                }}
              >
                <option value="">{school ? 'Other sport…' : 'Select a school first'}</option>
                {sports.map((s) => (
                  <option key={s.sport_id} value={s.sport_id}>{sportOptionLabel(s)}</option>
                ))}
              </select>
              {school && !scoutSportId && (
                <Input
                  className="mt-1.5"
                  aria-label="Sport"
                  value={sport}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const split = splitSportAndGender(raw);
                    setSport(raw);
                    if (split.gender) setGender(split.gender);
                  }}
                  onBlur={() => {
                    const split = splitSportAndGender(sport);
                    if (split.sport && split.sport !== sport) setSport(split.sport);
                  }}
                  placeholder="Sport not in Scout yet — type it (e.g. pickleball)"
                />
              )}
              <p className="mt-1 text-[10.5px] text-muted">Sports come from this school's Scout record. The store URL is built from the school and sport ids, so choose from the list when it's there.</p>
            </div>
            <div>
              <label htmlFor="create-store-gender" className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Gender</label>
              <select
                id="create-store-gender"
                className="h-8 w-full rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px]"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Not set</option>
                {STORE_GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <p className="mt-1.5 text-[11px] text-muted">
                Required. Stored on the store as its own field{gender === 'both' ? `. ${BOTH_GENDER_NOTE}` : '.'}
              </p>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Fundraiser name *</label>
              <Input value={fundraiserName} onChange={(e) => setFundraiserName(e.target.value)} placeholder="Lincoln Football Team Store" maxLength={140} />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Opens</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Admin email</label>
              <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="ad@school.edu" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Sales rep</label>
              <Input value={salesRepName} onChange={(e) => setSalesRepName(e.target.value)} placeholder="Name" className="mb-1.5" />
              <Input type="email" value={salesRepEmail} onChange={(e) => setSalesRepEmail(e.target.value)} placeholder="rep@ghostfitsports.com" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Quarterly commissions</label>
              <div className="flex flex-wrap gap-2 text-[12.5px]">
                <span className="rounded-[8px] border border-black/[0.08] bg-bg px-2.5 py-1.5 font-medium">10% team</span>
                <span className="rounded-[8px] border border-black/[0.08] bg-bg px-2.5 py-1.5 font-medium">7% sales rep</span>
              </div>
              <p className="mt-1.5 text-[11px] text-muted">
                Persisted on every store as <span className="font-mono">commissionRate 0.10</span> and <span className="font-mono">salesRepCommissionRate 0.07</span>. Stores stay open; payouts run each quarter.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ---- Step 2: Template & Colors ---- */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-6 shadow-card">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Product template *</h3>
                <p className="text-[11.5px] text-muted">
                  Which garments color-match may sell. Default is <span className="font-medium text-text">general</span>. Brand lineups exclude other vendors. School clones are hidden.
                </p>
              </div>
              <Link
                to={productLineupHref({ templateId: templateId || 'general' })}
                className="shrink-0 text-[11px] text-primary underline"
              >
                Edit garments →
              </Link>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {createTemplates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTemplateId(t.id || 'general')}
                  className={`rounded-[12px] border p-4 text-left transition-all ${templateId === t.id ? 'border-primary/60 bg-primary/[0.04] shadow-glow' : 'border-black/[0.07] hover:border-black/[0.14]'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-semibold tracking-tight">{t.name || t.id}</span>
                    {templateId === t.id && <CircleCheck className="h-4 w-4 text-primary" aria-hidden />}
                  </div>
                  <p className="mt-1 line-clamp-2 text-[11.5px] text-muted">{t.description || '—'}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {typeof t.styleCount === 'number' && <Badge className="bg-bg text-muted">{t.styleCount} styles</Badge>}
                    {(t.brands || []).slice(0, 3).map((b) => <Badge key={b} className="bg-primary/10 text-primary">{b}</Badge>)}
                  </div>
                </button>
              ))}
              {!templatesData && <Skeleton className="h-[110px] rounded-[12px]" />}
            </div>
          </div>

          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-6 shadow-card">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Sport artwork</h3>
                <p className="text-[11.5px] text-muted">
                  Designs (up to 2 per garment) plus the school-logo toggle. Auto uses the sport you picked on step 1. Logo-only skips designs.
                </p>
              </div>
              {resolvedArtworkId && artworkChoice.useDesignTemplates && (
                <Link
                  to={templateEditorHref({ templateId: resolvedArtworkId })}
                  className="shrink-0 text-[11px] text-primary underline"
                >
                  Edit artwork →
                </Link>
              )}
            </div>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {([
                { id: 'auto' as const, label: 'Auto from sport' },
                { id: 'logo-only' as const, label: 'Logo only' },
                { id: 'pick' as const, label: 'Pick artwork template' },
              ]).map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setArtworkMode(opt.id)}
                  className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${artworkMode === opt.id ? 'bg-text text-white' : 'border border-black/[0.08] bg-surface text-muted hover:bg-bg'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {artworkMode === 'auto' && (
              <p className="text-[11.5px] text-muted">
                {derivedArtworkId
                  ? <>Will use <span className="font-mono">{derivedArtworkId}</span>{artworkTpl ? ` (${artworkTpl.name || artworkTpl.id})` : ' — no matching doc yet, create will fall back to logo-only'}.</>
                  : 'No sport yet — create will stay in design mode and the creator will derive artwork from the sport field.'}
              </p>
            )}
            {artworkMode === 'logo-only' && (
              <p className="text-[11.5px] text-muted">School logo only — no AskBruce designs. Same path as athletics / general.</p>
            )}
            {artworkMode === 'pick' && (
              <div className="grid gap-3 sm:grid-cols-2">
                {artworkTemplates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setPickedArtworkId(t.id || '')}
                    className={`rounded-[12px] border p-4 text-left transition-all ${pickedArtworkId === t.id ? 'border-primary/60 bg-primary/[0.04] shadow-glow' : 'border-black/[0.07] hover:border-black/[0.14]'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-semibold tracking-tight">{t.name || t.id}</span>
                      {pickedArtworkId === t.id && <CircleCheck className="h-4 w-4 text-primary" aria-hidden />}
                    </div>
                    <p className="mt-1 font-mono text-[10.5px] text-subtle">{t.id}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {t.sport && <Badge className="bg-success/10 text-success">{t.sport}</Badge>}
                      {typeof t.styleCount === 'number' && <Badge className="bg-bg text-muted">{t.styleCount} garments</Badge>}
                      {!t.styleCount && t.styles && <Badge className="bg-bg text-muted">{t.styles.length} garments</Badge>}
                    </div>
                  </button>
                ))}
                {templatesData && artworkTemplates.length === 0 && (
                  <p className="col-span-2 text-[11.5px] text-muted">No sport artwork templates yet — add one in Assign to Products.</p>
                )}
              </div>
            )}
            {artworkChoice.useDesignTemplates && productTpl && (
              <p className={`mt-3 text-[11.5px] ${coverage.total > 0 && coverage.covered === coverage.total ? 'text-success' : 'text-muted'}`}>
                {artworkTpl
                  ? `${sport || 'This sport'} artwork covers ${coverage.covered} of ${coverage.total} garments on ${productTpl.name || productTpl.id}${coverage.missing.length ? ` — missing ${coverage.missing.join(', ')}` : ''}.`
                  : resolvedArtworkId
                    ? `${resolvedArtworkId} is not in the library yet — create will fall back to logo-only for missing artwork.`
                    : 'Pick a sport or an artwork template to see garment coverage.'}
              </p>
            )}
          </div>

          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-6 shadow-card">
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Personalization</h3>
            {modeControlsOn ? (
              <>
                <p className="mb-3 text-[11.5px] text-muted">
                  What buyers can add to the garments that have an open print spot. It can be changed later, even on a live store.
                </p>
                <label className="flex items-center gap-2 text-[12px]">
                  Buyers can add
                  <select
                    value={personalizationMode}
                    onChange={(e) => setPersonalizationMode(e.target.value as PersonalizationMode)}
                    className="h-8 rounded-md border border-black/10 bg-surface px-2 text-[12px]"
                  >
                    {PERSONALIZATION_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              </>
            ) : (
              <>
                <p className="mb-3 text-[11.5px] text-muted">
                  Adds paid name / number to the garments that have an open print spot. On by default for new stores — untick to leave it off.
                </p>
                <label className="flex items-center gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={personalizationHanger}
                    onChange={(e) => setPersonalizationHanger(e.target.checked)}
                  />
                  Name &amp; number personalization (hanger views)
                </label>
              </>
            )}
          </div>

          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-6 shadow-card">
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">School colors</h3>
            <p className="mb-3 text-[11.5px] text-muted">
              Enter the school&apos;s exact colours. Artwork uses these hexes; garments come in the closest stock family shown beside each.
              {colourNote === 'team' ? ' Pre-filled from this team\'s live store — confirm or adjust.' : colourNote === 'school' ? ' Pre-filled from the scout record — confirm or adjust.' : ''}
            </p>
            {([
              { label: 'Primary *', value: primary, set: setPrimary, idx: 0 },
              { label: 'Secondary *', value: secondary, set: setSecondary, idx: 1 },
              { label: 'Third (accent) *', value: tertiary, set: setTertiary, idx: 2 },
            ] as const).map(({ label, value, set, idx }) => {
              const rowError = colorError && (colorError.field === (['primary', 'secondary', 'tertiary'] as const)[idx] || colorError.pair?.includes((['primary', 'secondary', 'tertiary'] as const)[idx]));
              const fam = snapped[idx];
              return (
                <div key={label} className="mb-3 flex flex-wrap items-center gap-3 last:mb-0">
                  <span className="w-28 text-[11.5px] font-medium">{label}</span>
                  <input
                    type="color"
                    aria-label={`${label} colour`}
                    value={normalizeHex(value) || '#000000'}
                    onChange={(e) => set(e.target.value.toUpperCase())}
                    className={`h-8 w-10 cursor-pointer rounded-md border bg-transparent p-0.5 ${rowError ? 'border-red-500' : 'border-black/10'}`}
                  />
                  <input
                    type="text"
                    inputMode="text"
                    spellCheck={false}
                    maxLength={7}
                    placeholder="#0033A0"
                    aria-label={`${label} hex`}
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    onBlur={() => { const h = normalizeHex(value); if (h) set(h); }}
                    className={`h-8 w-24 rounded-md border bg-surface px-2 font-mono text-[12px] uppercase ${rowError ? 'border-red-500' : 'border-black/10'}`}
                  />
                  <span className="flex items-center gap-1.5 text-[11.5px] text-muted">
                    {fam ? (
                      <>
                        <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: fam.hex || undefined }} />
                        Garments: {fam.name}
                      </>
                    ) : palette.length === 0 && value ? <Skeleton className="h-4 w-24 rounded-full" /> : null}
                  </span>
                </div>
              );
            })}
            {colorError && colorError.code !== 'MISSING_COLOR' && (
              <p role="alert" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">{colorError.message}</p>
            )}
            {colorError?.code === 'MISSING_COLOR' && (
              <p className="mt-3 text-[11.5px] text-muted">All three colours are required.</p>
            )}
          </div>
        </div>
      )}

      {/* ---- Steps 3-5 need the live store ---- */}
      {liveStep && !store && storeId && (
        <div className="space-y-3"><Skeleton className="h-24 w-full rounded-[14px]" /><Skeleton className="h-24 w-full rounded-[14px]" /></div>
      )}

      {/* ---- Step 3: Logos ---- */}
      {step === 2 && store && (
        <WizardLogos store={store} refresh={async () => { await refreshStore(); }} />
      )}

      {/* ---- Step 4: Text overlays ---- */}
      {step === 3 && store && (
        <WizardText store={store} refresh={async () => { await refreshStore(); }} />
      )}

      {/* ---- Step 5: Preflight ---- */}
      {step === 4 && store && (
        <div className="space-y-3">
          {logos.length === 0 ? (
            <p className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">No logos yet — go back to step 3.</p>
          ) : (
            logos.map((logo) => (
              <WizardPreflightCard key={logo.id} store={store} logo={logo} refresh={async () => { await refreshStore(); }} />
            ))
          )}
          {cellStats.total > 0 && (
            <div className={`flex items-center gap-2 rounded-[12px] p-3 text-[12px] ${cellStats.allPass ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
              {cellStats.allPass ? <CircleCheck className="h-4 w-4" aria-hidden /> : <ShieldAlert className="h-4 w-4" aria-hidden />}
              {cellStats.pass}/{cellStats.total} cells passing{cellStats.allPass ? ' — ready to launch' : ''}
            </div>
          )}
          {!cellStats.allPass && cellStats.total > 0 && (
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={overridePreflight}>
              <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> Override preflight…
            </Button>
          )}
        </div>
      )}

      {/* ---- Step 6: Review & Launch ---- */}
      {step === 5 && store && (
        <div className="space-y-4">
          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-6 shadow-card">
            <h3 className="mb-3 text-[13px] font-semibold tracking-tight">Ready to launch?</h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px] sm:grid-cols-3">
              <div><dt className="text-muted">Store</dt><dd className="font-medium">{store.storeNumberDisplay || store.id}</dd></div>
              <div><dt className="text-muted">School</dt><dd className="font-medium">{store.schoolName || '—'}</dd></div>
              <div><dt className="text-muted">Sport</dt><dd className="font-medium capitalize">{store.sportType || '—'}</dd></div>
              <div><dt className="text-muted">Gender</dt><dd className="font-medium capitalize">{store.gender || '—'}</dd></div>
              <div><dt className="text-muted">Runs</dt><dd className="font-medium">{store.startDate?.slice(0, 10)} → {store.endDate ? store.endDate.slice(0, 10) : 'open'}</dd></div>
              <div><dt className="text-muted">Team / rep</dt><dd className="font-medium">10% / 7%</dd></div>
              <div>
                <dt className="text-muted">Colors</dt>
                <dd className="flex items-center gap-1.5 pt-0.5">
                  {store.primaryColor && <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: store.primaryColor }} />}
                  {store.secondaryColor && <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: store.secondaryColor }} />}
                </dd>
              </div>
              <div><dt className="text-muted">Logos</dt><dd className="font-medium">{logos.length}</dd></div>
              <div>
                <dt className="text-muted">Preflight</dt>
                <dd>
                  <Badge className={cellStats.allPass || store.preflightOverridden ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}>
                    {store.preflightOverridden ? 'overridden' : `${cellStats.pass}/${cellStats.total} pass`}
                  </Badge>
                </dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="lg" disabled={!!busy} onClick={approve}>
              <Rocket className="h-4 w-4" aria-hidden /> {busy === 'approve' ? 'Creating products…' : 'Approve & create Shopify products'}
            </Button>
            <Button size="lg" variant="secondary" onClick={() => navigate('/active-stores')}>
              Save for later
            </Button>
          </div>
          <p className="text-[11px] text-muted">
            Approving creates the live Shopify products + collection. “Save for later” keeps the store in Pending Review — resume any time from Team Stores.
          </p>
        </div>
      )}

      {/* Footer nav */}
      <div className="mt-6 flex items-center justify-between border-t border-black/[0.06] pt-4">
        <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(liveStep ? 2 : 0, s - 1))}>
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back
        </Button>
        {step < 5 && (
          step === 1 && !storeId ? (
            <Button disabled={!step2Ok || busy === 'create'} onClick={createDraft}>
              {busy === 'create' ? 'Creating draft…' : 'Create draft & continue'} <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          ) : (
            <Button
              disabled={(step === 0 && !step1Ok) || (step >= 2 && !store)}
              onClick={() => setStep((s) => Math.min(5, s + 1))}
            >
              Continue <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          )
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function WizardLogos({ store, refresh }: { store: TeamStoreDetail; refresh: () => Promise<void> }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const logos = store.logos ?? [];

  async function upload(files: FileList) {
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('logo', file);
        const res = await fetch(`/api/team-stores/${store.id}/logos`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new ApiError(res.status, body);
        await refresh();
      }
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error(errMsg(e, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(logoId: string) {
    try {
      await del(`/api/team-stores/${store.id}/logos/${encodeURIComponent(logoId)}`);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Remove failed'));
    }
  }

  return (
    <div className="space-y-3">
      <input ref={fileRef} type="file" accept="image/*,.svg,image/svg+xml" multiple className="hidden"
        onChange={(e) => { if (e.target.files?.length) void upload(e.target.files); e.target.value = ''; }} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-[14px] border-2 border-dashed border-black/[0.1] bg-surface/60 py-10 text-muted transition-colors hover:border-primary/40 hover:bg-primary/[0.03] hover:text-primary"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) void upload(e.dataTransfer.files); }}
      >
        <Plus className="h-6 w-6" aria-hidden />
        <span className="text-[12.5px] font-medium">{busy ? 'Uploading…' : 'Drop logos here, or click to browse'}</span>
        <span className="text-[10.5px]">PNG with transparency works best · 10 MB max each</span>
      </button>

      {logos.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {logos.map((logo) => (
            <div key={logo.id} className="flex items-center gap-3 rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
              <Thumbnail path={logo.gcsPath} alt={logo.label} className="h-14 w-14" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-medium">{logo.label}</p>
                <p className="font-mono text-[10px] text-subtle">{logo.id}</p>
              </div>
              <button className="text-subtle transition-colors hover:text-danger" onClick={() => logo.id && remove(logo.id)} title="Remove logo">
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WizardText({ store, refresh }: { store: TeamStoreDetail; refresh: () => Promise<void> }) {
  const { data: fontsData } = useSWR<FontsPayload>('/api/fonts', get);
  const fonts = fontNames(fontsData);
  const logos = store.logos ?? [];

  if (logos.length === 0) {
    return <p className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">No logos yet — go back to step 3.</p>;
  }
  return (
    <div className="space-y-3">
      <p className="text-[11.5px] text-muted">
        Optional: add team text under a logo. White and black composites are generated automatically; the right one is picked per garment color.
      </p>
      {logos.map((logo) => (
        <WizardTextRow key={logo.id} store={store} logo={logo} fonts={fonts} refresh={refresh} />
      ))}
    </div>
  );
}

function WizardTextRow({ store, logo, fonts, refresh }: { store: TeamStoreDetail; logo: TeamStoreLogo; fonts: string[]; refresh: () => Promise<void> }) {
  const [text, setText] = useState(logo.text || '');
  const [font, setFont] = useState(logo.font || 'default');
  const [busy, setBusy] = useState(false);
  const logoId = (logo.id ?? logo.logoId) as string;
  const composites = Object.entries(logo.compositeUrlByTextColor ?? {});
  const dirty = text !== (logo.text || '') || font !== (logo.font || 'default');

  async function apply() {
    setBusy(true);
    try {
      await post(`/api/team-stores/${store.id}/logos/${encodeURIComponent(logoId)}/text`, { text, font });
      toast.success(text ? 'Text applied' : 'Text cleared');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Text apply failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3.5 shadow-card">
      <div className="flex items-center gap-3">
        <Thumbnail path={logo.gcsPath} alt={logo.label} className="h-12 w-12" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium">{logo.label}</p>
          <div className="mt-1.5 flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={100} placeholder="e.g. LINCOLN FOOTBALL (blank = none)" className="flex-1" />
            <select className="h-8 rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[11.5px]" value={font} onChange={(e) => setFont(e.target.value)}>
              <option value="default">default</option>
              {fonts.filter((f) => f !== 'default').map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <Button size="sm" disabled={busy || !dirty} onClick={apply}>{busy ? 'Applying…' : 'Apply'}</Button>
          </div>
        </div>
      </div>
      {composites.length > 0 && (
        <div className="mt-2 flex items-center gap-2 pl-[60px]">
          <span className="text-[10.5px] text-subtle">Composites</span>
          {composites.map(([color, url]) => {
            const p = gcsToPath(url);
            return p ? <Thumbnail key={color} path={p} alt={`text ${color}`} className="h-10 w-10" /> : null;
          })}
        </div>
      )}
    </div>
  );
}

function WizardPreflightCard({ store, logo, refresh }: { store: TeamStoreDetail; logo: TeamStoreLogo; refresh: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const logoId = (logo.id ?? logo.logoId) as string;
  const entries = Object.entries(logo.preflight ?? {});

  async function runAll() {
    setBusy(true);
    try {
      // Recovery: Safari aborts silent fetches around ~50s and a multi-cell
      // bulk run legitimately takes that long. On a network-level drop the
      // run keeps going server-side and lands in one Firestore transaction —
      // poll the store doc for it instead of dead-ending.
      const r = await runBulkPreflightWithRecovery({
        logoId,
        run: () => post<{ cellCount?: number; passedCount?: number }>(
          `/api/team-stores/${store.id}/preflight-all/${encodeURIComponent(logoId)}`, undefined
        ),
        fetchStore: () => get<StoreLike>(`/api/active-stores/${store.id}`),
        isRealError: (e) => e instanceof ApiError,
        onRecovering: () => setRecovering(true),
      });
      toast.success(`${r.passed}/${r.total} cells pass${r.recovered ? ' (recovered after dropped connection)' : 'ed'}`);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, (e as Error)?.message || 'Preflight failed'));
    } finally {
      setBusy(false);
      setRecovering(false);
    }
  }

  return (
    <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3.5 shadow-card">
      <div className="flex items-center gap-3">
        <Thumbnail path={logo.gcsPath} alt={logo.label} className="h-12 w-12" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium">{logo.label}</p>
          <p className="font-mono text-[10px] text-subtle">{logoId}</p>
        </div>
        <Button size="sm" disabled={busy} onClick={runAll}>
          <Play className="h-3.5 w-3.5" aria-hidden /> {busy ? (recovering ? 'Recovering…' : 'Running…') : entries.length ? 'Re-run all cells' : 'Run all cells'}
        </Button>
      </div>
      <div className="mt-2.5">
        {entries.length === 0 ? (
          <p className="text-[11px] text-muted">Checks every garment color × print size this logo will be used on.</p>
        ) : (
          entries.sort(([a], [b]) => a.localeCompare(b)).map(([cellKey, entry]) => (
            <PreflightCellRow key={cellKey} storeId={store.id} logoId={logoId} cellKey={cellKey} entry={entry} />
          ))
        )}
      </div>
    </div>
  );
}
